import type { RequestFailure } from 'src/utils/requestError';
import type { AssistantDailyLimitScope } from 'src/types/assistant';

import { ASSISTANT_CODES } from 'src/types/assistant';

/** What a refused message means to the person: the words, whether the conversation is over, and whether signing in is the way on. */
export interface AssistantFailure {
  text: string;
  over: boolean;
  /** An anonymous visitor past their share of the day (api#441): the field gives way to "Sign in to continue". */
  signIn?: true;
}

/** Whose share of the day the refusal names, if it names one. */
const dailyLimitScopeOf = (failure: RequestFailure): AssistantDailyLimitScope | undefined => {
  const scope = (failure.payload as { scope?: unknown } | null | undefined)?.scope;

  return typeof scope === 'string' ? (scope as AssistantDailyLimitScope) : undefined;
};

/** What a refused message means to the person, by the api's code — and whether the conversation is over. */
export const describeAssistantFailure = (failure: RequestFailure): AssistantFailure => {
  switch (failure.code) {
    case ASSISTANT_CODES.conversationLimit:
      return { text: 'This conversation took every message it may. Start a new one.', over: true };
    case ASSISTANT_CODES.conversationNotFound:
      return { text: 'This conversation is not open any more. Start a new one.', over: true };
    case ASSISTANT_CODES.conversationBusy:
      return {
        text: 'The assistant is still answering your last message. Give it a moment.',
        over: false,
      };
    case ASSISTANT_CODES.dailyLimit: {
      const scope = dailyLimitScopeOf(failure);

      // An anonymous visitor past their own share or all visitors' (api#441): the limit is a nudge to
      // sign in (Tim, 2026-10-29), and says nothing of how many a signed-in person gets.
      if (scope === 'visitor' || scope === 'anonymous') {
        return {
          text: 'You have reached today’s limit of messages to the assistant. Sign in to continue.',
          over: false,
          signIn: true,
        };
      }

      // The words of the AI industry's free tiers (Tim, 2026-10-27), not "come back tomorrow".
      return {
        text: 'You have reached today’s limit of messages to the assistant. Your limit resets tomorrow.',
        over: false,
      };
    }
    case ASSISTANT_CODES.disabled:
      return { text: 'The assistant is switched off right now.', over: false };
    case ASSISTANT_CODES.modelUnavailable:
      return {
        text: 'The assistant is not available right now. Try again in a moment.',
        over: false,
      };
    default:
      break;
  }

  if (failure.status === 401 || failure.status === 403) {
    return { text: 'Sign in to ask the assistant.', over: false };
  }

  if (failure.status === 429) {
    return { text: 'Too many messages in a minute. Wait a little and try again.', over: false };
  }

  return { text: 'Something went wrong. Try again.', over: false };
};
